import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import { FacturaAPI } from '../api/facturas.api'
import { sileo } from 'sileo'

const BulkInvoiceContext = createContext()

export const useBulkInvoice = () => {
  const context = useContext(BulkInvoiceContext)
  if (!context) {
    throw new Error('useBulkInvoice must be used within a BulkInvoiceProvider')
  }
  return context
}

// Respuesta sincrónica del POST /factura/emitir: estos status significan que Tupana
// rechazó el documento de entrada (error de forma) y no hay nada que trackear/pollear.
const SYNC_IMMEDIATE_ERROR_STATUSES = ['invalid', 'temporary_error', 'permanent_error', 'incomplete']

// Estados de GET /factura/estado-local/:documentId que cierran el polling de ese documento.
const POLL_SUCCESS_STATUSES = ['issued', 'delivered', 'paid', 'unpaid']
const POLL_ERROR_STATUSES = ['rejected', 'cancelled', 'issue_failed', 'invalid', 'permanent_error']
// El SII todavía está procesando el documento -- hay que seguir consultando.
// "updated" se ve en la práctica (folio aún null) y no estaba en el contrato original.
const POLL_PENDING_STATUSES = ['pending', 'created', 'processing', 'updated']

const POLL_INTERVAL_MS = 4000
// El SII puede demorar bastante más que un par de minutos en validar un documento
// (se observó variabilidad real de varios minutos entre documentos del mismo lote).
const POLL_TIMEOUT_MS = 5 * 60 * 1000

export const BulkInvoiceProvider = ({ children }) => {
  const facturaApi = useMemo(() => new FacturaAPI(), [])

  const [isProcessing, setIsProcessing] = useState(false)
  const [documents, setDocuments] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState([])
  const [isFinished, setIsFinished] = useState(false)
  const [currentBusiness, setCurrentBusiness] = useState(null)
  const [uploadReport, setUploadReport] = useState(null)

  const processingRef = useRef(false)
  const resultsRef = useRef([])

  const delay = ms => new Promise(res => setTimeout(res, ms))

  const cleanObject = value => {
    if (Array.isArray(value)) return value.map(cleanObject)
    if (!value || typeof value !== 'object') return value
    return Object.fromEntries(
      Object.entries(value)
        .filter(
          ([_, currentValue]) =>
            currentValue !== undefined && currentValue !== null && currentValue !== ''
        )
        .map(([key, currentValue]) => [key, cleanObject(currentValue)])
    )
  }

  const extractErrorMessage = errors => {
    if (!errors) return ''
    if (typeof errors === 'string') return errors
    try {
      const flattened = Object.values(errors).flat().filter(Boolean)
      return flattened.length ? flattened.join(' | ') : JSON.stringify(errors)
    } catch {
      return JSON.stringify(errors)
    }
  }

  const buildDocumentPayload = (doc, business) => ({
    dte_type: { code: String(doc.dte_type?.code || '33') },
    document_issuer: {
      rut: business?.rut || '',
      business_name: business?.business_name || business?.name || '',
      email: business?.email || '',
      phone_number: business?.phone_number || business?.phoneNumber || '',
      address: business?.address || '',
      district: business?.district || '',
      city: business?.city || '',
      business_activity: business?.business_activity || '',
      activity_code:
        business?.activity_code && !isNaN(business.activity_code)
          ? Number(business.activity_code)
          : undefined
    },
    document_receiver: {
      rut: doc.document_receiver?.rut || '',
      business_name: doc.document_receiver?.business_name || '',
      contact: doc.document_receiver?.email || '',
      business_activity: doc.document_receiver?.business_activity || '',
      address: doc.document_receiver?.address || '',
      district: doc.document_receiver?.district || '',
      city: doc.document_receiver?.city || ''
    },
    details: (doc.details || []).map(detail => ({
      item_name: detail.item_name,
      quantity: detail.quantity === '' ? 0 : Number(detail.quantity || 0),
      unit_price: detail.unit_price === '' ? 0 : Number(detail.unit_price || 0),
      item_description: detail.item_description || '',
      discount_percent: detail.discount_percent === '' ? 0 : Number(detail.discount_percent || 0)
    })),
    references: (doc.references || []).map(ref => ({
      reference_type: ref.reference_type ? Number(ref.reference_type) : 1,
      reference_folio: ref.reference_folio ? Number(ref.reference_folio) : undefined,
      reference_date: ref.reference_date || undefined,
      reference_reason: ref.reference_reason || undefined,
      dte_type_code: ref.dte_type_code || undefined
    }))
  })

  // Pollea un único documento hasta que llegue a un estado terminal o se agote el timeout.
  // Se corre en paralelo para cada documento "en trámite", no en cadena.
  const pollDocumentEstado = async documentId => {
    const startedAt = Date.now()

    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      await delay(POLL_INTERVAL_MS)

      try {
        const statusRes = await facturaApi.getEstadoLocal(documentId)
        const status = statusRes?.data?.status

        if (POLL_SUCCESS_STATUSES.includes(status)) {
          return { status: 'success', message: 'Emitida correctamente', result: statusRes.data }
        }

        if (POLL_ERROR_STATUSES.includes(status)) {
          return {
            status: 'error',
            message: extractErrorMessage(statusRes?.data?.errors) || 'Factura rechazada por el SII',
            result: statusRes.data
          }
        }

        if (!POLL_PENDING_STATUSES.includes(status)) {
          console.warn(`estado-local devolvió un status no reconocido ("${status}"), se sigue consultando como pendiente`)
        }
        // pending / created / processing / updated (o cualquier status no reconocido) -> seguimos consultando
      } catch (pollErr) {
        console.error('Error polling estado-local', pollErr)
      }
    }

    // Se agotó el timeout: NO sabemos si el SII terminó de procesar o no -- esto NO
    // es un rechazo, es "todavía no tenemos confirmación". Debe tratarse distinto de 'error'.
    return {
      status: 'awaiting_confirmation',
      message: 'El SII todavía no confirmó el estado final -- revisa el listado en unos minutos',
      result: null
    }
  }

  const notifyProgress = (finalResults, index, entry) => {
    finalResults[index] = entry
    resultsRef.current = finalResults.filter(Boolean)
    setResults([...resultsRef.current])
    setCurrentIndex(resultsRef.current.length)
  }

  // Manda TODO el lote en un solo POST (documents: [...]) y separa, con la respuesta
  // sincrónica de Tupana, lo que ya fue rechazado de entrada de lo que quedó "en trámite".
  // Lo que queda en trámite se pollea en paralelo por document.id, no de a uno.
  const emitBatch = async (docsToProcess, business) => {
    const cleanedDocsPayload = docsToProcess.map(doc => cleanObject(buildDocumentPayload(doc, business)))
    const finalResults = new Array(docsToProcess.length)

    let responseDocuments = []
    try {
      const res = await facturaApi.emitirFactura(business.id, true, { documents: cleanedDocsPayload })
      responseDocuments = res?.data?.response_tupana?.documents || []
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Error al enviar el lote a la API'
      docsToProcess.forEach((doc, index) => {
        notifyProgress(finalResults, index, {
          index,
          doc,
          payload: { documents: [cleanedDocsPayload[index]] },
          status: 'error',
          message,
          result: err?.response?.data || null
        })
      })
      return finalResults
    }

    const pending = []

    docsToProcess.forEach((doc, index) => {
      const docPayload = { documents: [cleanedDocsPayload[index]] }
      const entry = responseDocuments.find(d => d.index === index) ?? responseDocuments[index]

      if (!entry) {
        notifyProgress(finalResults, index, {
          index, doc, payload: docPayload, status: 'error',
          message: 'Sin respuesta de Tupana para este documento', result: null
        })
        return
      }

      if (SYNC_IMMEDIATE_ERROR_STATUSES.includes(entry.status)) {
        notifyProgress(finalResults, index, {
          index, doc, payload: docPayload, status: 'error',
          message: extractErrorMessage(entry.errors) || 'Rechazado por errores de forma', result: entry
        })
        return
      }

      const documentId = entry?.document?.id
      if (!documentId) {
        notifyProgress(finalResults, index, {
          index, doc, payload: docPayload, status: 'error',
          message: 'No se pudo obtener el ID del documento en Tupana', result: entry
        })
        return
      }

      pending.push({ index, doc, docPayload, documentId })
    })

    // Todos los pendientes se pollean a la vez (Promise.all), no en fila.
    await Promise.all(
      pending.map(async ({ index, doc, docPayload, documentId }) => {
        const outcome = await pollDocumentEstado(documentId)
        notifyProgress(finalResults, index, { index, doc, payload: docPayload, ...outcome })
      })
    )

    return finalResults
  }

  const startEmission = useCallback(async (docsToProcess, business, reportMetadata = null) => {
    if (processingRef.current) return

    setIsProcessing(true)
    setDocuments(docsToProcess)
    setUploadReport(reportMetadata)
    setResults([])
    setIsFinished(false)
    setCurrentBusiness(business)
    setCurrentIndex(0)

    processingRef.current = true
    resultsRef.current = []

    const processLoop = async () => {
      await emitBatch(docsToProcess, business)

      // Final report and Save to DB
      const finalReport = {
        empresa_id: business?.id,
        nombre_empresa: business?.business_name,
        fecha_reporte: new Date().toISOString(),
        total_documentos: docsToProcess.length,
        documentos_exitosos: resultsRef.current.filter(r => r.status === 'success').length,
        documentos_fallidos: resultsRef.current.filter(r => r.status === 'error').length,
        documentos_pendientes_confirmacion: resultsRef.current.filter(r => r.status === 'awaiting_confirmation').length,
        detalle_emision: resultsRef.current.map(res => ({
          fila: res.index + 1,
          receptor_rut: res.doc.document_receiver?.rut,
          receptor_nombre: res.doc.document_receiver?.business_name,
          tipo_dte: res.doc.dte_type?.code,
          estado_final: res.status,
          motivo_resultado: res.message,
          payload_enviado: res.payload,
          documento_emitido: res.result
        }))
      }

      try {
        await facturaApi.createBulkInvoiceUpload(business.id, finalReport)
        console.log('✅ Reporte guardado exitosamente en la DB')
      } catch (error) {
        console.error('❌ Error al guardar el reporte en la DB:', error)
      }

      setCurrentIndex(docsToProcess.length)
      setIsFinished(true)
      setIsProcessing(false) // Note: isProcessing goes false but results stay
      processingRef.current = false

      // Notify user
      if (finalReport.documentos_fallidos > 0) {
        sileo.warning({
          title: 'Carga Masiva Finalizada',
          description: `Se procesaron ${docsToProcess.length} documentos. ${finalReport.documentos_exitosos} exitosos y ${finalReport.documentos_fallidos} con errores.`
        })
      } else if (finalReport.documentos_pendientes_confirmacion > 0) {
        sileo.warning({
          title: 'Carga Masiva Finalizada',
          description: `${finalReport.documentos_exitosos} de ${docsToProcess.length} confirmados. ${finalReport.documentos_pendientes_confirmacion} todavía esperando confirmación del SII -- revisa el listado en unos minutos.`
        })
      } else {
        sileo.success({
          title: '¡Emisión Masiva Completada!',
          description: `Se han emitido exitosamente ${docsToProcess.length} documentos.`
        })
      }
    }

    processLoop()
  }, [facturaApi])

  const resetEmission = useCallback(() => {
    if (processingRef.current) return
    setIsProcessing(false)
    setDocuments([])
    setUploadReport(null)
    setCurrentIndex(0)
    setResults([])
    setIsFinished(false)
    setCurrentBusiness(null)
  }, [])

  const value = {
    isProcessing,
    documents,
    uploadReport,
    setUploadReport,
    currentIndex,
    results,
    isFinished,
    currentBusiness,
    startEmission,
    resetEmission
  }

  return (
    <BulkInvoiceContext.Provider value={value}>
      {children}
    </BulkInvoiceContext.Provider>
  )
}
